// MKT-PASTE #3: shared source of truth for the homepage hero poster so the
// <img> and the <head> preload always agree on URL + srcset (a mismatch is
// exactly the LCP bug fixed for the variety hero in MKT-#2). Two invisible
// wins vs. the old single raw-CloudFront 1920w poster:
//   1. srcset: mobile viewports get the ~15KB 1280w variant instead of the
//      27KB 1920w (only 1280w + 1920w exist on CloudFront; 960w/640w 403).
//   2. same-origin: routed through the /cdn/ proxy so the LCP image reuses
//      the document HTTP/2 connection instead of a second cross-origin TLS
//      handshake (same pattern as the variety-hero fix).
// Hero behavior is unchanged: poster paints first, video rolls in after load.

import { sameOriginPhotoUrl } from "@/lib/catalog/photo-url";

const CDN_BASE =
  "https://d154w98knb4f7r.cloudfront.net/marketing/cayambe-volcano-hero-poster";

export const HERO_POSTER_1280 = sameOriginPhotoUrl(`${CDN_BASE}-1280w.avif`);
export const HERO_POSTER_1920 = sameOriginPhotoUrl(`${CDN_BASE}-1920w.avif`);

// The hero is full-bleed (100vw). With sizes="100vw" the browser picks 1280w
// on phones (small viewport x DPR) and 1920w on wide desktops.
export const HERO_POSTER_SRCSET = `${HERO_POSTER_1280} 1280w, ${HERO_POSTER_1920} 1920w`;
export const HERO_POSTER_SIZES = "100vw";

// src/href fallback for engines that ignore srcset / imagesrcset.
export const HERO_POSTER_FALLBACK = HERO_POSTER_1920;

// MKT-#9: sizes for the full-bleed CINEMATIC photo heroes (metro + /about
// family). The hero is a background under a heavy CSS dark overlay, so it
// does not need retina-DPR sharpness. Under-declaring the mobile slot caps
// phones on the small 480w (~10KB) / 800w (~23KB) AVIF - the homepage-poster
// class that scores 98 - while desktop keeps the full 1280w/1920w cinematic
// asset. The page's hero <link rel=preload> MUST use this same value as
// imageSizes so the preloaded variant is exactly the one painted.
export const CINEMATIC_HERO_SIZES = "(max-width: 767px) 60vw, 100vw";

// AVIF preload srcset for a cinematic hero at a same-origin /cdn/ base
// (matches MarketingPicture's AVIF <source>). Pass the asset's real width
// ladder for cap-clipped sources (e.g. packing = [480, 800]).
export function cinematicHeroAvifSrcSet(
  cdnBase: string,
  widths: ReadonlyArray<number> = [480, 800, 1280, 1920],
): string {
  return widths.map((w) => `${cdnBase}-${w}w.avif ${w}w`).join(", ");
}

// A ready-to-spread <link rel=preload> props object for a cinematic hero.
export function cinematicHeroPreloadProps(
  cdnBase: string,
  widths?: ReadonlyArray<number>,
) {
  return {
    rel: "preload" as const,
    as: "image" as const,
    type: "image/avif",
    imageSrcSet: cinematicHeroAvifSrcSet(cdnBase, widths),
    imageSizes: CINEMATIC_HERO_SIZES,
    fetchPriority: "high" as const,
  };
}
